import React, { type ReactNode } from 'react';
import { alpha, type Theme } from '@mui/material';
import Box from '../../atoms/Box';

export type BadgeTone = 'success' | 'warn' | 'neutral' | 'info' | 'danger';

export interface ToneBadgeProps {
  tone?: BadgeTone;
  children: ReactNode;
}

function toneColors(theme: Theme, tone: BadgeTone) {
  const dark = theme.palette.mode === 'dark';
  const pick = (main: string, light: string, darkShade: string) => ({
    bg: alpha(main, dark ? 0.16 : 0.12),
    line: alpha(main, dark ? 0.38 : 0.35),
    fg: dark ? light : darkShade,
  });
  switch (tone) {
    case 'success':
      return pick(
        theme.palette.success.main,
        theme.palette.success.light,
        theme.palette.success.dark
      );
    case 'warn':
      return pick(
        theme.palette.warning.main,
        theme.palette.warning.light,
        theme.palette.warning.dark
      );
    case 'info':
      return pick(
        theme.palette.info.main,
        theme.palette.info.light,
        theme.palette.info.dark
      );
    case 'danger':
      return pick(
        theme.palette.error.main,
        theme.palette.error.light,
        theme.palette.error.dark
      );
    default:
      return {
        bg: dark ? 'rgba(255,255,255,0.07)' : theme.palette.action.selected,
        line: theme.palette.divider,
        fg: theme.palette.text.secondary,
      };
  }
}

/** Small pill whose colour carries meaning only: status, never decoration. */
export default function ToneBadge({
  tone = 'neutral',
  children,
}: ToneBadgeProps) {
  return (
    <Box
      component="span"
      sx={(theme) => {
        const c = toneColors(theme, tone);
        return {
          display: 'inline-flex',
          alignItems: 'center',
          height: 22,
          px: 1,
          borderRadius: '999px',
          fontSize: 11,
          fontWeight: 600,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          bgcolor: c.bg,
          color: c.fg,
          border: `1px solid ${c.line}`,
        };
      }}
    >
      {children}
    </Box>
  );
}
